import { format } from "date-fns"

import { parseBusySlotDate } from "@/lib/calendar-busy-slots"
import type { GetEventsBusySlots200SlotsItem } from "openapi/api/model"

/**
 * Reading a booking clash.
 *
 * Canvas 15c: "the clash check is the reason this screen exists: it names the
 * conflicting event and offers the first clear slot as one tap, rather than
 * making you open the calendar." The old dialog only ever raised a browser
 * `confirm()` listing the overlaps at submit time, which said what was wrong
 * but left the fixing to you.
 */

export type Clash = {
  /** What the busy slot is called, when the provider gives it a name. */
  name: string
  /** "14:30 – 15:00", in the viewer's own clock. */
  when: string
  end: Date
}

/** Clashes worth naming, earliest first. Unparseable slots are dropped. */
export function readClashes(slots: GetEventsBusySlots200SlotsItem[]): Clash[] {
  return slots
    .flatMap((slot) => {
      const start = parseBusySlotDate(slot.start)
      const end = parseBusySlotDate(slot.end)
      if (!start || !end) return []
      return [
        {
          name: slot.eventName?.trim() || "Busy",
          when: `${format(start, "HH:mm")} – ${format(end, "HH:mm")}`,
          end,
        },
      ]
    })
    .sort((a, b) => a.end.getTime() - b.end.getTime())
}

/** A quarter of an hour, which is the grain every proposal lands on. */
const QUARTER_MINUTES = 15

/**
 * The first clear start after everything that clashes: a quarter of an hour
 * past the last clash, rounded up to the next quarter so the suggestion reads
 * as a time someone would actually pick.
 *
 * Returns `undefined` when nothing clashes, or when the next clear slot falls
 * outside the day and so cannot be offered as a same-day move.
 */
export function firstClearStart(clashes: Clash[]): string | undefined {
  const last = clashes[clashes.length - 1]
  if (!last) return undefined

  const proposed = new Date(last.end.getTime() + QUARTER_MINUTES * 60000)
  const minutes = proposed.getMinutes()
  const overshoot = minutes % QUARTER_MINUTES
  if (overshoot) proposed.setMinutes(minutes + (QUARTER_MINUTES - overshoot), 0, 0)
  else proposed.setSeconds(0, 0)

  if (proposed.getDate() !== last.end.getDate()) return undefined
  return format(proposed, "HH:mm")
}

/** Keeps a move to a later start from collapsing the meeting's length. */
export function shiftedEnd(start: string, end: string, nextStart: string): string {
  const minutesOf = (value: string) => {
    const [hours, mins] = value.split(":").map(Number)
    return Number.isFinite(hours) && Number.isFinite(mins) ? hours * 60 + mins : undefined
  }

  const from = minutesOf(start)
  const to = minutesOf(end)
  const next = minutesOf(nextStart)
  if (from === undefined || to === undefined || next === undefined) return end

  const length = Math.max(to - from, QUARTER_MINUTES)
  const total = Math.min(next + length, 23 * 60 + 59)
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`
}
